import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  ErrorOutline,
  Refresh,
  Schedule,
  SensorsOff,
  WarningAmber,
} from '@mui/icons-material';
import { useEnergy } from '../../context/EnergyContext';
import { useViewContext } from '../../context/ViewContext';
import { formatValue } from '../../utils/energyViewModel';
import { zonedInstant, zonedParts } from '../../utils/energyRanges';

const STATUS = {
  normal: { color: 'success', icon: <CheckCircle fontSize="small" />, label: 'Normal' },
  good: { color: 'success', icon: <CheckCircle fontSize="small" />, label: 'Good' },
  live: { color: 'success', icon: <CheckCircle fontSize="small" />, label: 'Live' },
  warning: { color: 'warning', icon: <WarningAmber fontSize="small" />, label: 'Warning' },
  limited: { color: 'warning', icon: <WarningAmber fontSize="small" />, label: 'Limited' },
  faulted: { color: 'error', icon: <ErrorOutline fontSize="small" />, label: 'Faulted' },
  fault: { color: 'error', icon: <ErrorOutline fontSize="small" />, label: 'Fault' },
  stale: { color: 'default', icon: <Schedule fontSize="small" />, label: 'Stale' },
  offline: { color: 'default', icon: <SensorsOff fontSize="small" />, label: 'Offline' },
  unavailable: { color: 'default', icon: <SensorsOff fontSize="small" />, label: '-' },
};

export const EnergyStatusChip = ({ status, label, size = 'small' }) => {
  const key = String(status || 'unavailable').toLowerCase();
  const config = STATUS[key] || {
    color: 'info',
    icon: <CheckCircle fontSize="small" />,
    label: status || '-',
  };
  return (
    <Chip
      size={size}
      color={config.color}
      variant={config.color === 'default' ? 'outlined' : 'filled'}
      icon={config.icon}
      label={String(label || config.label).replace(/\bunavailable\b/gi, '-')}
      sx={{ textTransform: 'capitalize', fontWeight: 600 }}
    />
  );
};

export const MetricTile = ({ label, value, unit, state, measure, provenance, accent = 'primary.main', helper, precision = 3 }) => (
  <Paper variant="outlined" sx={{ p: 1.5, minHeight: 108, borderTop: 3, borderTopColor: accent }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.04em' }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
      {formatValue(value, unit, { maximumFractionDigits: precision })}
    </Typography>
    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
      {(measure?.status || state) && <Typography variant="caption" color="text.secondary">{String(measure?.status || state).replace(/\bunavailable\b/gi, '-')}</Typography>}
      {provenance && <Chip size="small" variant="outlined" label={provenance} sx={{ height: 20, fontSize: 10 }} />}
    </Stack>
    {measure?.coverage?.coveragePct != null && measure.status !== 'complete' && <Typography variant="caption" color="text.secondary" display="block">{formatValue(measure.coverage.coveragePct, '%')} coverage</Typography>}
    {helper && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{helper}</Typography>}
  </Paper>
);

export const TimeRangeControl = () => {
  const { range, setRange, timezone, model } = useEnergy();
  const [customOpen, setCustomOpen] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [rangeError, setRangeError] = useState(null);
  const applyCustom = () => {
    const parse = value => {
      const parts = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
      if (!parts) throw new Error('Enter both dates and times.');
      const local = { year: Number(parts[1]), month: Number(parts[2]), day: Number(parts[3]), hour: Number(parts[4]), minute: Number(parts[5]), second: 0 };
      const instant = zonedInstant(local, timezone);
      const actual = zonedParts(instant, timezone);
      if (Object.keys(local).some(key => actual[key] !== local[key])) throw new Error('That local time does not exist in the selected timezone.');
      return instant;
    };
    try {
      const start = parse(customStart); const end = parse(customEnd);
      if (end <= start || end - start > 366 * 86400000) throw new Error('Select a positive period no longer than 366 days.');
      setRange({ start: new Date(start).toISOString(), end: new Date(end).toISOString() }); setRangeError(null); setCustomOpen(false);
    } catch (error) { setRangeError(error.message); }
  };
  return (
    <Stack spacing={0.5} alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
      <ButtonGroup size="small" aria-label="Energy history range">
        {['hour', 'day', 'week', 'month', 'year', 'billing_period'].map((item) => (
          <Button
            key={item}
            variant={range === item ? 'contained' : 'outlined'}
            onClick={() => setRange(item)}
            aria-pressed={range === item}
          >
            {item === 'billing_period' ? 'Billing period' : item[0].toUpperCase() + item.slice(1)}
          </Button>
        ))}
        <Button onClick={() => setCustomOpen(value => !value)} variant={typeof range === 'object' ? 'contained' : 'outlined'}>Custom</Button>
      </ButtonGroup>
      {customOpen && <Stack spacing={1}>
        <TextField type="datetime-local" size="small" label={`From (${timezone})`} InputLabelProps={{ shrink: true }} value={customStart} onChange={event => setCustomStart(event.target.value)} />
        <TextField type="datetime-local" size="small" label={`To (${timezone})`} InputLabelProps={{ shrink: true }} value={customEnd} onChange={event => setCustomEnd(event.target.value)} />
        <Button onClick={applyCustom}>Apply period</Button>
        {rangeError && <Typography role="alert" variant="caption" color="error">{rangeError}</Typography>}
      </Stack>}
      <Typography variant="caption" color="text.secondary">Calendar periods · {timezone}</Typography>
      {model.history?.range && <Typography variant="caption" color="text.secondary">
        {displayTime(model.history.range.start, timezone)} – {displayTime(model.history.range.end, timezone)}
      </Typography>}
    </Stack>
  );
};

const displayTime = (value, timezone) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('en-US', { timeZone: timezone });
};

export const EnergyPageHeader = ({ title, subtitle, back = false, actions, showRange = true }) => {
  const {
    model, refreshing, refresh, errors, hasScope, historyLoading, liveCommitSeq,
  } = useEnergy();
  const { setView } = useViewContext();
  return (
    <Box sx={{ mb: 2 }}>
      {back && (
        <Button size="small" startIcon={<ArrowBack />} onClick={() => setView('dashboard')} sx={{ mb: 1 }}>
          Back to Dashboard
        </Button>
      )}
      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h5" component="h1" fontWeight={600}>{title}</Typography>
              <EnergyStatusChip status={model.meta.quality} label={`Quality: ${model.meta.quality}`} />
              {(model.meta.balanceCutoffTime || model.meta.sourceTime) && <Typography variant="caption" color="text.secondary">
                {model.meta.freshness?.clockBasis === 'simulator' ? 'Last simulator reading (EST): ' : 'Last reading: '}
                {displayTime(model.meta.balanceCutoffTime || model.meta.sourceTime, model.meta.freshness?.clockBasis === 'simulator' ? 'Etc/GMT+5' : model.meta.timezone || 'UTC')}
              </Typography>}
              {refreshing && <CircularProgress size={18} aria-label="Refreshing energy data" />}
            </Stack>
            {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{subtitle}</Typography>}
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
            {actions}
            {showRange && <TimeRangeControl />}
            <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={() => refresh()}>
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Paper>
      {historyLoading && liveCommitSeq != null && <Alert severity="info" sx={{ mt: 1.5 }}>
        Current readings update live. Loading energy totals and charts…
      </Alert>}
      {!hasScope && (
        <Alert severity="info" sx={{ mt: 1.5 }}>Select an authorized customer account to load energy data.</Alert>
      )}
      {hasScope && errors.some((error) => error.source === 'latest') && !model.meta.sourceTime && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>Energy data could not be loaded. Use Refresh to retry.</Alert>
      )}
      {hasScope && errors.some(error => ['energy', 'session', 'connection'].includes(error.source)) &&
        <Alert severity="warning" sx={{ mt: 1.5 }}>{errors.find(error => ['energy', 'session', 'connection'].includes(error.source)).message}</Alert>}
    </Box>
  );
};

export const SectionCard = ({ title, subtitle, action, children, sx }) => (
  <Paper variant="outlined" sx={{ p: 2, ...sx }}>
    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </Box>
      {action}
    </Stack>
    <Divider sx={{ my: 1.5 }} />
    {children}
  </Paper>
);

const CHART_COLORS = {
  solarKw: '#f57c00',
  loadKw: '#7b1fa2',
  bessKw: '#388e3c',
  gridKw: '#1976d2',
  socPct: '#00897b',
};

const finiteChartValue = (value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));

export const EnergyLineChart = ({ series = [], fields, height = 220, emptyLabel = '-' }) => {
  const { model, timezone } = useEnergy();
  const visibleFields = fields.filter((field) => series.some((point) => finiteChartValue(point[field.key])));
  const validTimes = series.map((point) => Date.parse(point.time)).filter(Number.isFinite);
  const range = model.history?.range;
  const start = Date.parse(range?.start) || Math.min(...validTimes);
  const end = Date.parse(range?.end) || Math.max(...validTimes);
  if (!series.length || !visibleFields.length || !Number.isFinite(start) || !Number.isFinite(end)) {
    return <Box sx={{ height, display: 'grid', placeItems: 'center', bgcolor: 'grey.50', border: '1px dashed', borderColor: 'divider' }}>
      <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>
    </Box>;
  }
  const axisFor = (field) => field.key === 'socPct' ? 'percent' : 'power';
  const axes = Object.fromEntries(['power', 'percent'].map((axis) => {
    const values = visibleFields.filter((field) => axisFor(field) === axis).flatMap((field) =>
      series.flatMap((point) => [point[field.key], point.extrema?.[field.key]?.min, point.extrema?.[field.key]?.max])
        .filter(finiteChartValue).map(Number));
    return [axis, axis === 'percent' ? { min: 0, max: 100 } : { min: Math.min(0, ...values), max: Math.max(1, ...values) }];
  }));
  const xFor = (time) => 50 + ((Date.parse(time) - start) / Math.max(1, end - start)) * 700;
  const yFor = (value, axis) => 16 + (axes[axis].max - Number(value)) / Math.max(1, axes[axis].max - axes[axis].min) * 150;
  const segmentsFor = (field) => {
    const segments = [];
    let segment = [];
    let previousTime = null;
    const maxGap = (model.history?.bucketSeconds || 10) * 1500;
    series.forEach((point) => {
      const time = Date.parse(point.time);
      if (!finiteChartValue(point[field.key]) || !Number.isFinite(time) ||
          (previousTime !== null && time - previousTime > maxGap)) {
        if (segment.length) segments.push(segment);
        segment = [];
      }
      if (finiteChartValue(point[field.key]) && Number.isFinite(time)) {
        segment.push(`${xFor(point.time)},${yFor(point[field.key], axisFor(field))}`);
      }
      previousTime = time;
    });
    if (segment.length) segments.push(segment);
    return segments;
  };
  const shortTime = (time) => new Date(time).toLocaleString('en-US', {
    timeZone: timezone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
  return <Box>
    <Box component="svg" viewBox="0 0 800 212" role="img" aria-label={visibleFields.map((field) => field.label).join(', ')} sx={{ width: '100%', height, display: 'block' }}>
      {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
        const y = 16 + fraction * 150;
        return <g key={fraction}><line x1="50" y1={y} x2="750" y2={y} stroke="#e0e0e0" />
          <text x="46" y={y + 4} textAnchor="end" fontSize="10" fill="#666">{formatValue(axes.power.max - fraction * (axes.power.max - axes.power.min), null, { maximumFractionDigits: 1 })}</text>
          {visibleFields.some((field) => axisFor(field) === 'percent') && <text x="756" y={y + 4} fontSize="10" fill="#00897b">{100 - fraction * 100}%</text>}
        </g>;
      })}
      {[0, 0.5, 1].map((fraction) => <text key={fraction} x={50 + fraction * 700} y="192" textAnchor={fraction === 0 ? 'start' : fraction === 1 ? 'end' : 'middle'} fontSize="10" fill="#666">{shortTime(start + fraction * (end - start))}</text>)}
      {visibleFields.map((field) => <g key={field.key}>
        {segmentsFor(field).map((segment, index) => <polyline key={index} fill="none" stroke={field.color || CHART_COLORS[field.key] || '#455a64'} strokeWidth="2" points={segment.join(' ')} strokeLinejoin="round" />)}
        {series.filter((point) => finiteChartValue(point[field.key])).map((point) => <g key={point.time}>
          {finiteChartValue(point.extrema?.[field.key]?.min) && <line x1={xFor(point.time)} x2={xFor(point.time)} y1={yFor(point.extrema[field.key].min, axisFor(field))} y2={yFor(point.extrema[field.key].max, axisFor(field))} stroke={field.color || CHART_COLORS[field.key] || '#455a64'} opacity=".25" />}
          <circle cx={xFor(point.time)} cy={yFor(point[field.key], axisFor(field))} r="3" fill={field.color || CHART_COLORS[field.key] || '#455a64'} opacity=".2">
            <title>{shortTime(Date.parse(point.time))}: {field.label} {formatValue(point[field.key], axisFor(field) === 'percent' ? '%' : 'kW')}; min {formatValue(point.extrema?.[field.key]?.min)}, max {formatValue(point.extrema?.[field.key]?.max)}; coverage {point.coverage?.[field.key]?.coveredSeconds ?? '-'} / {point.coverage?.[field.key]?.expectedSeconds ?? '-'} seconds</title>
          </circle>
        </g>)}
      </g>)}
    </Box>
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
      {visibleFields.map((field) => <Stack key={field.key} direction="row" spacing={0.5} alignItems="center"><Box sx={{ width: 10, height: 10, bgcolor: field.color || CHART_COLORS[field.key] || '#455a64', borderRadius: '50%' }} /><Typography variant="caption">{field.label}</Typography></Stack>)}
    </Stack>
    <Typography variant="caption" color="text.secondary" display="block">{timezone} · Bucket averages; shading shows min/max. Gaps indicate missing samples.</Typography>
    {visibleFields.some(field => model.history?.periodSummary?.[field.key]) && <Typography variant="caption" color="text.secondary" display="block">
      Selected period coverage: {visibleFields.filter((field) => model.history.periodSummary[field.key]).map((field) => `${field.label} ${formatValue(model.history.periodSummary[field.key].coveragePct, '%')}`).join(' · ')}
    </Typography>}
  </Box>;
};

export const ProvenanceNote = ({ children }) => (
  <Alert severity="info" icon={false} sx={{ mt: 1.5, '& .MuiAlert-message': { width: '100%' } }}>
    <Typography variant="caption">{children}</Typography>
  </Alert>
);
